// ==============================================
// Player Profile Page
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { playersAPI } from '../services/api';
import { idFromSlug, playerPath } from '../utils/slug';

const SITE_URL = process.env.REACT_APP_SITE_URL || '';

interface PlayerData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  external_profile_url: string | null;
  status: string;
  email_verified: boolean;
  level_id: number | null;
  level_slug: string | null;
  level_name: string | null;
  created_at: string;
  total_matches: number;
  total_wins: number;
  total_losses: number;
  win_rate: number;
  tournaments_played: number;
}

interface RecentMatch {
  id: number;
  match_date: string;
  opponent_id: number;
  opponent_name: string;
  opponent_first_name: string;
  opponent_last_name: string;
  opponent_avatar_url: string | null;
  opponent_level_slug: string | null;
  opponent_level_name: string | null;
  result: 'win' | 'loss' | 'draw';
  player1_score: number;
  player2_score: number;
  tournament_title: string | null;
  match_type_slug: string | null;
  match_type_name: string | null;
}

interface TournamentResult {
  title: string;
  start_date: string;
  final_position: number | null;
  points_earned: number | null;
  club_name: string;
}

const getLevelBadgeClass = (slug?: string | null) => {
  switch (slug) {
    case 'elite':         return 'cat-elite';
    case 'profesionisti': return 'cat-elite';
    case 'open':          return 'cat-a';
    case 'avansati':      return 'cat-b';
    case 'hobby':         return 'cat-c';
    case 'amator':        return 'cat-d';
    default:              return 'cat-d';
  }
};

function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  const [y, mo, d] = iso.split('-').map(Number);
  const target = new Date(y, mo - 1, d).getTime();
  if (Number.isNaN(target)) return null;
  const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime();
  return Math.max(0, Math.round((today - target) / 86400000));
}

function fmtShortDate(iso: string): string {
  const [y, mo, day] = iso.split('-').map(Number);
  return new Date(y, mo - 1, day).toLocaleDateString('ro-RO', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function fmtLastMatchAgo(iso?: string | null): string {
  const days = daysSince(iso);
  if (days === null) return 'Niciun meci';
  if (days === 0) return 'Astăzi';
  if (days === 1) return 'Ieri';
  if (days < 31) return `acum ${days} zile`;

  const months = Math.floor(days / 30);
  if (months < 12) return `acum ${months} ${months === 1 ? 'lună' : 'luni'}`;

  const years = Math.floor(months / 12);
  return `acum ${years} ${years === 1 ? 'an' : 'ani'}`;
}

function externalUrlLabel(value?: string | null): string {
  if (!value) return '';
  try {
    const url = new URL(value);
    return url.hostname.replace(/^www\./, '');
  } catch {
    return value.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
}

const MpTeaserCard: React.FC = () => (
  <div className="stat-card2 h-100" style={{
    border: '1px dashed rgba(33,37,123,.28)',
    background: 'linear-gradient(135deg, rgba(239,246,255,.85), rgba(255,255,255,.96))',
  }}>
    <div className="stat-icon" style={{ background: 'linear-gradient(135deg,#94a3b8,#64748b)' }}>MP</div>
    <div>
      <div className="stat-label">MP</div>
      <div className="stat-value" style={{ filter: 'blur(3px)', color: 'var(--tt-primary)' }}>000</div>
      <div style={{ color: 'var(--tt-text-muted)', fontSize: '.72rem', lineHeight: 1.35, marginTop: '.25rem' }}>
        MP va fi afișat mai târziu, după ce strângem mai multe informații. Momentan nu facem calcule.
      </div>
    </div>
  </div>
);

const BackToPlayersButton: React.FC<{ onClick: () => void; compact?: boolean }> = ({ onClick, compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '.45rem',
      border: '1px solid var(--tt-border)',
      background: 'var(--tt-surface)',
      color: 'var(--tt-text)',
      borderRadius: 999,
      padding: compact ? '.45rem .8rem' : '.55rem .95rem',
      fontSize: '.85rem',
      fontWeight: 800,
      boxShadow: 'var(--sh-sm)',
      cursor: 'pointer',
      width: compact ? '100%' : undefined,
    }}
  >
    <span
      aria-hidden="true"
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--tt-surface-2)',
        color: 'var(--tt-primary)',
        fontSize: '1rem',
        lineHeight: 1,
      }}
    >
      ‹
    </span>
    Înapoi la jucători
  </button>
);

const Player: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const playerId = idFromSlug(id || '0');

  const [player, setPlayer] = useState<PlayerData | null>(null);
  const [recentMatches, setRecentMatches] = useState<RecentMatch[]>([]);
  const [tournamentResults, setTournamentResults] = useState<TournamentResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPlayerProfile = useCallback(async () => {
    if (!playerId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await playersAPI.getProfile(playerId);
      if (response.success && response.data) {
        setPlayer(response.data.profile as PlayerData);
        setRecentMatches((response.data.recent_matches as RecentMatch[]) || []);
        setTournamentResults((response.data.tournament_results as TournamentResult[]) || []);
      } else {
        setError('Player not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load player profile');
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => { loadPlayerProfile(); }, [loadPlayerProfile]);

  if (loading) {
    return (
      <div className="page">
        <div className="container page-inner">
          <div className="tt-loading">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <span>Loading player profile...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="page">
        <div className="container page-inner">
          <div className="alert alert-danger">{error || 'Player not found'}</div>
          <BackToPlayersButton onClick={() => navigate('/players')} />
        </div>
      </div>
    );
  }

  const initials = `${player.first_name?.[0] ?? ''}${player.last_name?.[0] ?? ''}`.toUpperCase();
  const fullName = `${player.first_name} ${player.last_name}`.trim();
  const avatarSrc = player.avatar_url ? `${SITE_URL}${player.avatar_url}` : null;
  const memberSince = new Date(player.created_at).toLocaleDateString('ro-RO', { year: 'numeric', month: 'long', day: 'numeric' });
  const resultColor = (r: string) => r === 'win' ? '#10b981' : r === 'loss' ? '#ef4444' : '#6b7280';
  const resultLetter = (r: RecentMatch['result']) => r === 'win' ? 'V' : r === 'loss' ? 'I' : 'E';
  const recentFormMatches = recentMatches.slice(0, 5);
  const matchTypeLabel = (slug?: string | null, name?: string | null) => {
    if (name) return name;
    if (slug === 'competitive') return 'Competitiv';
    if (slug === 'tournament') return 'Turneu';
    return 'Amical';
  };
  const lastMatch = recentMatches[0] ?? null;
  const lastMatchAgo = fmtLastMatchAgo(lastMatch?.match_date);
  const externalProfileLabel = externalUrlLabel(player.external_profile_url);

  return (
    <div className="page">
      <div className="container page-inner">
        <div className="mb-3">
          <BackToPlayersButton onClick={() => navigate('/players')} />
        </div>

        {/* Banner */}
        <div className="profile-banner player-profile-hero fade-up mb-4" style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle at 85% 15%, rgba(255,255,255,.2), transparent 28%)',
            pointerEvents: 'none',
          }} />
          <div className="player-profile-hero-inner d-flex flex-column flex-lg-row justify-content-between gap-4">
            <div className="player-profile-identity d-flex gap-3 align-items-start">
              <div
                className="avatar avatar-lg"
                style={{
                  width: 92, height: 92, minWidth: 92,
                  background: avatarSrc ? 'transparent' : 'rgba(255,255,255,.14)',
                  color: '#fff', overflow: 'hidden',
                  border: '3px solid rgba(255,255,255,.28)',
                  boxShadow: '0 16px 40px rgba(0,0,0,.22)',
                  fontSize: '1.85rem',
                }}
              >
                {avatarSrc ? (
                  <img src={avatarSrc} alt={fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : initials}
              </div>
              <div>
                <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                  <h1 className="mb-0" style={{ fontSize: '2rem', lineHeight: 1.1 }}>{fullName}</h1>
                  <span className={`badge ${player.status === 'active' ? 'status-active' : 'status-inactive'}`}>{player.status}</span>
                </div>
                <div className="mb-3">
                  {player.level_name && (
                    <span className={`badge ${getLevelBadgeClass(player.level_slug)}`}>{player.level_name}</span>
                  )}
                </div>
                <div style={{ color: 'rgba(255,255,255,.72)', fontSize: '.92rem', lineHeight: 1.8 }}>
                  {player.email && <div>{player.email}</div>}
                  {player.phone && <div>{player.phone}</div>}
                  <div>Membru din {memberSince}</div>
                  {player.external_profile_url && (
                    <a
                      href={player.external_profile_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '.45rem',
                        marginTop: '.45rem',
                        color: '#fff',
                        textDecoration: 'none',
                        fontWeight: 800,
                        background: 'rgba(255,255,255,.16)',
                        border: '1px solid rgba(255,255,255,.22)',
                        borderRadius: 999,
                        padding: '.38rem .75rem',
                        lineHeight: 1.1,
                      }}
                    >
                      Profil extern
                      <span style={{ color: 'rgba(255,255,255,.72)', fontWeight: 700 }}>{externalProfileLabel}</span>
                      <span aria-hidden="true">↗</span>
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="player-profile-hero-stats row g-3" style={{ minWidth: 'min(100%, 360px)' }}>
              {[
                { label: 'Victorii',  value: String(player.total_wins) },
                { label: 'Meciuri',   value: String(player.total_matches) },
                { label: 'Rata V',    value: `${player.win_rate}%` },
                { label: 'Turnee',    value: String(player.tournaments_played) },
                { label: 'Ultimul meci', value: lastMatchAgo },
              ].map(s => (
                <div key={s.label} className="col-6">
                  <div style={{ background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 12, padding: '12px 14px' }}>
                    <div className="stat-label" style={{ color: 'rgba(255,255,255,.56)' }}>{s.label}</div>
                    <div className="stat-value" style={{ color: '#fff' }}>{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats cards */}
        <div className="player-profile-stats row g-3 mb-4">
          {[
            { icon: '🏆', color: 'linear-gradient(135deg,#10b981,#059669)', label: 'Victorii',       value: String(player.total_wins) },
            { icon: '📊', color: 'linear-gradient(135deg,#21257B,#1a1e62)', label: 'Total meciuri',  value: String(player.total_matches) },
            { icon: '⚡', color: 'linear-gradient(135deg,#EF8628,#d67320)', label: 'Rata victorii',  value: `${player.win_rate}%` },
            { icon: '🏓', color: 'linear-gradient(135deg,#7c3aed,#5b21b6)', label: 'Turnee jucate',  value: String(player.tournaments_played) },
            { icon: '🗓️', color: 'linear-gradient(135deg,#06b6d4,#0891b2)', label: 'Ultimul meci',  value: lastMatchAgo },
          ].map(s => (
            <div key={s.label} className="col-md-3 col-6 fade-up">
              <div className="stat-card2 h-100">
                <div className="stat-icon" style={{ background: s.color }}>{s.icon}</div>
                <div>
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-value">{s.value}</div>
                </div>
              </div>
            </div>
          ))}
          <div className="col-md-6 col-lg-4 fade-up">
            <MpTeaserCard />
          </div>
        </div>

        <div className="row g-4">
          {/* Recent matches */}
          <div className="col-lg-7 fade-up">
            <div className="entity-card h-100">
              <div className="entity-card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-1">Forma Recenta</h5>
                    <p className="text-muted mb-0" style={{ fontSize: '.875rem' }}>Ultimele 5 meciuri</p>
                  </div>
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    {recentFormMatches.map((m) => (
                      <div
                        key={`form-${m.id}`}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          background: resultColor(m.result),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: '700',
                          fontSize: '1rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,.1)',
                        }}
                      >
                        {resultLetter(m.result)}
                      </div>
                    ))}
                  </div>
                </div>
                {recentFormMatches.length === 0 ? (
                  <div className="tt-empty" style={{ padding: '2rem 1rem' }}>
                    <div className="tt-empty-icon">🏓</div>
                    <h5>Niciun meci încă</h5>
                    <p>Rezultatele recente vor apărea aici după primul meci.</p>
                  </div>
                ) : (
                  <div className="d-grid gap-2">
                    {recentFormMatches.map((m, idx) => (
                      <div key={m.id} className="dashboard-recent-match" style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '.75rem',
                        background: idx % 2 === 0 ? '#f8fafc' : '#fff',
                        borderRadius: '8px',
                        gap: '1rem',
                      }}>
                        <div style={{ width: 32, height: 32, borderRadius: 6, background: resultColor(m.result), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '.85rem', flexShrink: 0 }}>
                          {resultLetter(m.result)}
                        </div>
                        <Link
                          to={playerPath({ id: m.opponent_id, first_name: m.opponent_first_name || '', last_name: m.opponent_last_name || '' })}
                          className="dashboard-opponent-avatar"
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            flexShrink: 0,
                            background: m.opponent_avatar_url ? 'transparent' : 'linear-gradient(135deg, var(--tt-primary), var(--tt-accent))',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 900,
                            textDecoration: 'none',
                            boxShadow: '0 2px 8px rgba(15,23,42,.12)',
                          }}
                        >
                          {m.opponent_avatar_url
                            ? <img src={`${SITE_URL}${m.opponent_avatar_url}`} alt={m.opponent_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : `${m.opponent_first_name?.[0] ?? ''}${m.opponent_last_name?.[0] ?? ''}`.toUpperCase()
                          }
                        </Link>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <Link
                            to={playerPath({ id: m.opponent_id, first_name: m.opponent_first_name || '', last_name: m.opponent_last_name || '' })}
                            className="fw-semibold"
                            style={{
                              fontSize: '.92rem',
                              color: 'var(--tt-text)',
                              textDecoration: 'none',
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            vs {m.opponent_name}
                          </Link>
                          <div className="dashboard-match-meta" style={{ display: 'flex', alignItems: 'center', gap: '.35rem', flexWrap: 'wrap', marginTop: '.18rem' }}>
                            {m.opponent_level_name && <span className={`badge ${getLevelBadgeClass(m.opponent_level_slug)}`}>{m.opponent_level_name}</span>}
                            <span className="text-muted" style={{ fontSize: '.78rem' }}>{m.tournament_title || matchTypeLabel(m.match_type_slug, m.match_type_name)}</span>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="fw-bold" style={{ fontSize: '.95rem' }}>{m.player1_score}-{m.player2_score}</div>
                          <div className="text-muted" style={{ fontSize: '.75rem' }}>{fmtShortDate(m.match_date)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Link to="/matches" className="btn btn-outline-primary btn-sm w-100 mt-3">
                  Vezi Toate Meciurile
                </Link>
              </div>
            </div>
          </div>

          {/* Tournament results + actions */}
          <div className="col-lg-5 fade-up">
            <div className="entity-card h-100">
              <div className="entity-card-body">
                <h5 className="mb-3">Rezultate turnee</h5>
                {tournamentResults.length === 0 ? (
                  <div className="tt-empty" style={{ padding: '2rem 1rem' }}>
                    <div className="tt-empty-icon">🏆</div>
                    <h5>Niciun turneu finalizat</h5>
                    <p>Participările și clasările vor apărea aici.</p>
                  </div>
                ) : (
                  <div className="d-grid gap-2 mb-3">
                    {tournamentResults.slice(0, 5).map((t, idx) => (
                      <div key={idx} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid var(--tt-border)' }}>
                        <div>
                          <div className="fw-semibold" style={{ fontSize: '.88rem' }}>{t.title}</div>
                          <div className="text-muted" style={{ fontSize: '.78rem' }}>{t.club_name}</div>
                        </div>
                        <div className="text-end">
                          {t.final_position && <div className="fw-bold">#{t.final_position}</div>}
                          <div className="text-muted" style={{ fontSize: '.75rem' }}>{t.start_date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="d-grid gap-2 mt-3">
                  <Link to="/rankings" className="btn btn-primary">Vezi Clasamente</Link>
                  <BackToPlayersButton onClick={() => navigate('/players')} compact />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Player;
